import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Container, Typography, useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import ProductCard from "../components/ProductCard/ProductCard";
import { useHomeProducts } from "../components/hooks/useHomeProducts";
import { Icon, ImgPh } from "../components/liquid/LiquidPrimitives";
import { useCategoryShowcase } from "../hooks/useCategoryShowcase";
import type { Product } from "../types/Product";
import { useEditableBlock, useTemplateConfig } from "./templateConfigContext";
import type { BloqueHomeDTO } from "./types";
import { Link } from "react-router-dom";
import { trackMetric } from "@ecommerce/analytics";

const crearCssAcotadoAlBloque = (bloque: BloqueHomeDTO): string | null => {
    const vchCssCustom = bloque.estilo?.vchCssCustom?.trim();
    if (!vchCssCustom) return null;
    const vchSelectorBloque = `[data-bloque="${bloque.uniIdBloque}"]`;
    return `@scope (${vchSelectorBloque}) {\n${vchCssCustom}\n}`;
};

const completarProductos = (productos: Product[], intMinimo: number) => {
    if (productos.length === 0) return [];
    if (productos.length >= intMinimo) return productos;

    const lista = [...productos];
    let intIndice = 0;
    while (lista.length < intMinimo) {
        lista.push(productos[intIndice % productos.length]);
        intIndice += 1;
    }
    return lista;
};

function useColumnas() {
    const { config } = useTemplateConfig();
    const bitWide = useMediaQuery("(min-width:1536px)");
    const bitDesktop = useMediaQuery("(min-width:1200px)");
    const bitTablet = useMediaQuery("(min-width:768px)");

    if (bitWide) return config.layout.intColumnasWide;
    if (bitDesktop) return config.layout.intColumnasDesktop;
    if (bitTablet) return config.layout.intColumnasTablet;
    return config.layout.intColumnasMobile;
}

function ContenedorBloque({
    bloque,
    children,
    bitOcultarTitulo = false,
}: {
    bloque: BloqueHomeDTO;
    children: React.ReactNode;
    bitOcultarTitulo?: boolean;
}) {
    const { config } = useTemplateConfig();
    const bitMobile = useMediaQuery("(max-width:768px)");
    const vchCssAcotado = crearCssAcotadoAlBloque(bloque);

    return (
        <section
            data-bloque={bloque.uniIdBloque}
            style={{
                padding: bitMobile
                    ? `${Math.max(28, config.theme.espaciado.intSeccion * 0.65)}px 16px`
                    : `${config.theme.espaciado.intSeccion}px ${config.theme.espaciado.intContainerPadding}px`,
                ...(bloque.estilo?.vchColorFondo && {
                    backgroundColor: bloque.estilo.vchColorFondo,
                }),
                ...(bloque.estilo?.vchColorTexto && { color: bloque.estilo.vchColorTexto }),
            }}
        >
            {vchCssAcotado && React.createElement("style", null, vchCssAcotado)}
            <div style={{ maxWidth: config.layout.intContentMaxWidth, margin: "0 auto" }}>
                {!bitOcultarTitulo && (bloque.config.vchEyebrow || bloque.config.vchTitulo) && (
                    <div style={{ marginBottom: 26 }}>
                        {bloque.config.vchEyebrow && (
                            <div className="eyebrow" style={{ marginBottom: 10 }}>
                                {bloque.config.vchEyebrow}
                            </div>
                        )}
                        {bloque.config.vchTitulo && (
                            <h2 className="h-section" style={{ margin: 0 }}>
                                {bloque.config.vchTitulo}
                            </h2>
                        )}
                    </div>
                )}
                {children}
            </div>
        </section>
    );
}

function HeroTexto({
    bloque,
    bitOscuro,
    bitMobile,
}: {
    bloque: BloqueHomeDTO;
    bitOscuro: boolean;
    bitMobile: boolean;
}) {
    const { config } = useTemplateConfig();
    return (
        <>
            {bloque.config.vchEyebrow && (
                <div
                    className="eyebrow"
                    style={{
                        color: bitOscuro
                            ? config.theme.colores.vchMarcaSecundaria
                            : config.theme.colores.vchMarcaPrincipal,
                        marginBottom: 18,
                    }}
                >
                    {bloque.config.vchEyebrow}
                </div>
            )}
            <h1
                style={{
                    margin: 0,
                    maxWidth: 700,
                    fontFamily: config.theme.tipografia.vchFontFamilyTitulos,
                    fontSize: bitMobile
                        ? Math.max(34, config.theme.tipografia.intTituloHero * 0.62)
                        : config.theme.tipografia.intTituloHero,
                    lineHeight: 0.98,
                    fontWeight: config.theme.tipografia.intPesoBold,
                    letterSpacing: 0,
                }}
            >
                {bloque.config.vchTitulo}
            </h1>
            {bloque.config.vchSubtitulo && (
                <p
                    style={{
                        maxWidth: 540,
                        margin: "20px 0 0",
                        fontSize: bitMobile ? 15 : 17,
                        lineHeight: 1.65,
                        color:
                            bloque.estilo?.vchColorTexto ??
                            (bitOscuro
                                ? "rgba(255,255,255,0.76)"
                                : config.theme.colores.vchTextoSecundario),
                    }}
                >
                    {bloque.config.vchSubtitulo}
                </p>
            )}
            <a
                href={bloque.config.vchLinkBoton ?? "/productos"}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 30,
                    padding: "14px 24px",
                    borderRadius: bloque.estilo?.intRadioBoton ?? config.theme.radios.intPill,
                    background:
                        bloque.estilo?.vchColorBotonFondo ??
                        (bitOscuro
                            ? config.theme.colores.vchMarcaPrincipal
                            : config.theme.botones.vchPrimaryBg),
                    color:
                        bloque.estilo?.vchColorBotonTexto ??
                        (bitOscuro ? "#111111" : config.theme.botones.vchPrimaryTexto),
                    textDecoration: "none",
                    fontWeight: 700,
                    fontSize: 14,
                }}
            >
                {bloque.config.vchTextoBoton ?? "Ver productos"} <Icon.arrowUpRight />
            </a>
        </>
    );
}

function HeroCarousel({
    imagenes,
    brightness = 1,
    style,
}: {
    imagenes: string[];
    brightness?: number;
    style?: React.CSSProperties;
}) {
    const [intActual, setIntActual] = useState(0);

    useEffect(() => {
        if (imagenes.length <= 1) return;
        const id = setInterval(() => setIntActual((i) => (i + 1) % imagenes.length), 5000);
        return () => clearInterval(id);
    }, [imagenes.length]);

    return (
        <div style={{ position: "relative", overflow: "hidden", ...style }}>
            {imagenes.length === 0 ? (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        color: "rgba(0,0,0,0.18)",
                        pointerEvents: "none",
                    }}
                >
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                    </svg>
                    <span style={{ fontSize: 12, fontFamily: "inherit", fontWeight: 500 }}>
                        Sin imagen
                    </span>
                </div>
            ) : (
                imagenes.map((src, i) => (
                    <div
                        key={i}
                        style={{
                            position: "absolute",
                            inset: 0,
                            opacity: i === intActual ? 1 : 0,
                            transition: "opacity 0.8s ease",
                        }}
                    >
                        <img
                            src={src}
                            alt=""
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                filter: `brightness(${brightness})`,
                                display: "block",
                            }}
                        />
                    </div>
                ))
            )}
            {imagenes.length > 1 && (
                <div
                    style={{
                        position: "absolute",
                        bottom: 14,
                        left: 0,
                        right: 0,
                        display: "flex",
                        justifyContent: "center",
                        gap: 6,
                        zIndex: 10,
                    }}
                >
                    {imagenes.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIntActual(i)}
                            style={{
                                width: i === intActual ? 20 : 8,
                                height: 8,
                                borderRadius: 4,
                                background:
                                    i === intActual
                                        ? "rgba(255,255,255,0.9)"
                                        : "rgba(255,255,255,0.4)",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                transition: "all 0.3s",
                                flexShrink: 0,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function Hero({ bloque, productos }: { bloque: BloqueHomeDTO; productos: Product[] }) {
    const { config } = useTemplateConfig();
    const bitMobile = useMediaQuery("(max-width:900px)");
    const productoHero = productos.find((producto) => producto.urlImagenPrincipal);
    const bitOscuro = bloque.config.bitOscuro || config.theme.colores.vchFondoPagina === "#0f0f0f";

    // vchTipoHero en componentes define el layout; vchVariante en el bloque puede sobreescribirlo
    const tipoHero =
        (bloque.config.vchVariante as typeof config.componentes.vchTipoHero | undefined) ??
        config.componentes.vchTipoHero ??
        "split";

    const bloqueKey = bloque.uniIdBloque;
    const vchCssAcotado = crearCssAcotadoAlBloque(bloque);
    const cssTag = vchCssAcotado ? React.createElement("style", null, vchCssAcotado) : null;

    // Imágenes: usa vchImagenes (array JSON) si existe, sino vchImagen (legacy)
    const imagenes: string[] = useMemo(() => {
        if (bloque.config.vchImagenes) {
            try {
                return JSON.parse(bloque.config.vchImagenes) as string[];
            } catch {
                /* */
            }
        }
        const fallback = bloque.config.vchImagen || productoHero?.urlImagenPrincipal;
        return fallback ? [fallback] : [];
    }, [bloque.config.vchImagenes, bloque.config.vchImagen, productoHero?.urlImagenPrincipal]);

    // ── FULLWIDTH: imagen de fondo, texto encima ──────────────────────────────
    if (tipoHero === "fullwidth") {
        return (
            <section
                data-bloque={bloqueKey}
                style={{
                    position: "relative",
                    overflow: "hidden",
                    minHeight: bitMobile ? 560 : 680,
                }}
            >
                {cssTag}
                {imagenes.length > 0 && (
                    <HeroCarousel
                        imagenes={imagenes}
                        brightness={bitOscuro ? 0.5 : 0.65}
                        style={{ position: "absolute", inset: 0, height: "100%" }}
                    />
                )}
                <div
                    style={{
                        position: "relative",
                        zIndex: 2,
                        maxWidth: config.layout.intContentMaxWidth,
                        margin: "0 auto",
                        padding: bitMobile
                            ? "80px 24px 60px"
                            : "120px clamp(40px, 8vw, 120px) 100px",
                        color: "#ffffff",
                    }}
                >
                    <HeroTexto bloque={bloque} bitOscuro={true} bitMobile={bitMobile} />
                </div>
            </section>
        );
    }

    // ── CENTRADO: texto centrado, imagen de fondo suave ───────────────────────
    if (tipoHero === "centrado") {
        const bitTieneImagenFondo = imagenes.length > 0;
        const vchColorContenido =
            bloque.estilo?.vchColorTexto ??
            (bitTieneImagenFondo
                ? config.theme.footer.vchTitulo
                : config.theme.colores.vchTextoPrincipal);
        return (
            <section
                data-bloque={bloqueKey}
                style={{
                    position: "relative",
                    overflow: "hidden",
                    minHeight: bitMobile ? 520 : 620,
                    padding: bitMobile ? "60px 24px 70px" : "100px 32px 110px",
                    background: bloque.estilo?.vchColorFondo ?? config.theme.gradientes.vchHero,
                    textAlign: "center",
                    color: vchColorContenido,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {cssTag}
                {bitTieneImagenFondo && (
                    <>
                        <HeroCarousel
                            imagenes={imagenes}
                            brightness={0.72}
                            style={{ position: "absolute", inset: 0, height: "100%" }}
                        />
                        <div
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                inset: 0,
                                background: config.theme.colores.vchOverlay,
                                opacity: bitOscuro ? 1 : 0.78,
                                zIndex: 1,
                                pointerEvents: "none",
                            }}
                        />
                    </>
                )}
                <div
                    style={{
                        position: "relative",
                        zIndex: 2,
                        width: "100%",
                        maxWidth: 760,
                        margin: "0 auto",
                    }}
                >
                    {bloque.config.vchEyebrow && (
                        <div
                            className="eyebrow"
                            style={{
                                color:
                                    bloque.estilo?.vchColorTexto ??
                                    (bitTieneImagenFondo
                                        ? config.theme.colores.vchMarcaSecundaria
                                        : config.theme.colores.vchMarcaPrincipal),
                                marginBottom: 18,
                            }}
                        >
                            {bloque.config.vchEyebrow}
                        </div>
                    )}
                    <h1
                        style={{
                            margin: 0,
                            fontFamily: config.theme.tipografia.vchFontFamilyTitulos,
                            fontSize: bitMobile
                                ? Math.max(36, config.theme.tipografia.intTituloHero * 0.65)
                                : config.theme.tipografia.intTituloHero,
                            lineHeight: 1.02,
                            fontWeight: config.theme.tipografia.intPesoBold,
                        }}
                    >
                        {bloque.config.vchTitulo}
                    </h1>
                    {bloque.config.vchSubtitulo && (
                        <p
                            style={{
                                maxWidth: 560,
                                margin: "20px auto 0",
                                fontSize: bitMobile ? 15 : 18,
                                lineHeight: 1.65,
                                color:
                                    bloque.estilo?.vchColorTexto ??
                                    (bitTieneImagenFondo
                                        ? `color-mix(in srgb, ${vchColorContenido} 82%, transparent)`
                                        : config.theme.colores.vchTextoSecundario),
                            }}
                        >
                            {bloque.config.vchSubtitulo}
                        </p>
                    )}
                    <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
                        <a
                            href={bloque.config.vchLinkBoton ?? "/productos"}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "14px 28px",
                                borderRadius:
                                    bloque.estilo?.intRadioBoton ?? config.theme.radios.intPill,
                                background:
                                    bloque.estilo?.vchColorBotonFondo ??
                                    config.theme.botones.vchPrimaryBg,
                                color:
                                    bloque.estilo?.vchColorBotonTexto ??
                                    config.theme.botones.vchPrimaryTexto,
                                textDecoration: "none",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            {bloque.config.vchTextoBoton ?? "Ver productos"} <Icon.arrowUpRight />
                        </a>
                    </div>
                </div>
            </section>
        );
    }

    // ── MINIMAL: solo texto + botón, sin contenedor ni imagen ─────────────────
    if (tipoHero === "minimal") {
        return (
            <section
                data-bloque={bloqueKey}
                style={{ padding: bitMobile ? "48px 24px 40px" : "72px 32px 56px" }}
            >
                {cssTag}
                <div style={{ maxWidth: config.layout.intContentMaxWidth, margin: "0 auto" }}>
                    {bloque.config.vchEyebrow && (
                        <div
                            className="eyebrow"
                            style={{
                                color: config.theme.colores.vchMarcaPrincipal,
                                marginBottom: 14,
                            }}
                        >
                            {bloque.config.vchEyebrow}
                        </div>
                    )}
                    <h1
                        style={{
                            margin: 0,
                            maxWidth: 820,
                            fontFamily: config.theme.tipografia.vchFontFamilyTitulos,
                            fontSize: bitMobile
                                ? Math.max(36, config.theme.tipografia.intTituloHero * 0.65)
                                : config.theme.tipografia.intTituloHero,
                            lineHeight: 1,
                            fontWeight: config.theme.tipografia.intPesoBold,
                            borderBottom: `3px solid ${config.theme.colores.vchMarcaPrincipal}`,
                            paddingBottom: 20,
                        }}
                    >
                        {bloque.config.vchTitulo}
                    </h1>
                    {bloque.config.vchSubtitulo && (
                        <p
                            style={{
                                maxWidth: 540,
                                margin: "18px 0 0",
                                fontSize: bitMobile ? 15 : 17,
                                lineHeight: 1.65,
                                color: config.theme.colores.vchTextoSecundario,
                            }}
                        >
                            {bloque.config.vchSubtitulo}
                        </p>
                    )}
                    <a
                        href={bloque.config.vchLinkBoton ?? "/productos"}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 28,
                            padding: "12px 22px",
                            borderRadius:
                                bloque.estilo?.intRadioBoton ?? config.theme.radios.intPill,
                            background:
                                bloque.estilo?.vchColorBotonFondo ??
                                config.theme.botones.vchPrimaryBg,
                            color:
                                bloque.estilo?.vchColorBotonTexto ??
                                config.theme.botones.vchPrimaryTexto,
                            textDecoration: "none",
                            fontWeight: 700,
                            fontSize: 14,
                        }}
                    >
                        {bloque.config.vchTextoBoton ?? "Ver productos"} <Icon.arrowUpRight />
                    </a>
                </div>
            </section>
        );
    }

    // ── SPLIT (default): imagen + texto lado a lado ───────────────────────────
    return (
        <section
            data-bloque={bloqueKey}
            style={{ padding: bitMobile ? "18px 16px 28px" : "28px 32px 42px" }}
        >
            {cssTag}
            <div
                style={{
                    maxWidth: config.layout.intContentMaxWidth,
                    margin: "0 auto",
                    minHeight: bitMobile ? 500 : 520,
                    borderRadius: config.theme.radios.intXl,
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateColumns: bitMobile
                        ? "1fr"
                        : "minmax(0, 1.05fr) minmax(320px, 0.95fr)",
                    alignItems: "center",
                    gap: bitMobile ? 26 : 44,
                    padding: bitMobile ? "40px 26px" : "70px 76px",
                    background:
                        bloque.estilo?.vchColorFondo ??
                        (bitOscuro
                            ? config.theme.gradientes.vchDestacado
                            : config.theme.gradientes.vchHero),
                    color:
                        bloque.estilo?.vchColorTexto ??
                        (bitOscuro
                            ? config.theme.botones.vchPrimaryTexto
                            : config.theme.colores.vchTextoPrincipal),
                    boxShadow: config.theme.sombras.vchLg,
                }}
            >
                <div>
                    <HeroTexto bloque={bloque} bitOscuro={bitOscuro} bitMobile={bitMobile} />
                </div>
                <div
                    style={{
                        height: bitMobile ? 260 : 390,
                        borderRadius: config.theme.radios.intLg,
                        overflow: "hidden",
                        background: config.theme.cards.vchFondo,
                        border: `1px solid ${config.theme.cards.vchBorde}`,
                        boxShadow: config.theme.cards.vchSombra,
                    }}
                >
                    <HeroCarousel
                        imagenes={imagenes}
                        brightness={bitOscuro ? 0.92 : 1}
                        style={{ height: "100%" }}
                    />
                </div>
            </div>
        </section>
    );
}

function Categorias({ bloque }: { bloque: BloqueHomeDTO }) {
    const { config } = useTemplateConfig();
    const intCantidad = bloque.config.intCantidad ?? 4;
    const { images } = useCategoryShowcase(intCantidad);
    const bitMobile = useMediaQuery("(max-width:900px)");
    const bitBannerCategorias = bloque.config.vchVariante === "banner";

    if (images.length === 0) return null;

    const contenido = (
        <>
            {bitBannerCategorias && (
                <div
                    style={{
                        marginBottom: 28,
                        padding: bitMobile ? "34px 24px" : "54px 58px",
                        borderRadius: config.theme.radios.intXl,
                        background: config.theme.gradientes.vchHero,
                        boxShadow: config.theme.sombras.vchMd,
                    }}
                >
                    {bloque.config.vchEyebrow && (
                        <div className="eyebrow" style={{ marginBottom: 12 }}>
                            {bloque.config.vchEyebrow}
                        </div>
                    )}
                    <h1
                        style={{
                            margin: 0,
                            maxWidth: 760,
                            fontFamily: config.theme.tipografia.vchFontFamilyTitulos,
                            fontSize: bitMobile
                                ? Math.max(34, config.theme.tipografia.intTituloHero * 0.58)
                                : config.theme.tipografia.intTituloHero,
                            lineHeight: 1,
                            letterSpacing: 0,
                        }}
                    >
                        {bloque.config.vchTitulo}
                    </h1>
                    {bloque.config.vchSubtitulo && (
                        <p
                            style={{
                                maxWidth: 620,
                                margin: "16px 0 0",
                                color: config.theme.colores.vchTextoSecundario,
                                lineHeight: 1.65,
                            }}
                        >
                            {bloque.config.vchSubtitulo}
                        </p>
                    )}
                </div>
            )}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        bitMobile || config.componentes.vchTipoCategoryGrid !== "mosaico"
                            ? `repeat(${bitMobile ? 1 : Math.min(images.length, 4)}, minmax(0, 1fr))`
                            : "1.2fr 0.8fr",
                    gap: config.theme.espaciado.intGridGap,
                }}
            >
                {images.map((image, index) => (
                    <Link
                        key={`${image.title}-${index}`}
                        to={image.link}
                        onClick={() => {
                            const categorySku = image.link.match(/^\/?categoria\/([^/?#]+)/)?.[1];
                            if (categorySku) {
                                trackMetric("CATEGORY_CLICK", {
                                    entitySku: decodeURIComponent(categorySku),
                                });
                            }
                        }}
                        style={{
                            position: "relative",
                            minHeight: bitBannerCategorias
                                ? index === 0 && !bitMobile
                                    ? 420
                                    : 280
                                : index === 0 && !bitMobile
                                  ? 360
                                  : 230,
                            borderRadius: config.theme.radios.intLg,
                            overflow: "hidden",
                            color: "#ffffff",
                            textDecoration: "none",
                            background: config.theme.gradientes.vchDestacado,
                            boxShadow: config.theme.sombras.vchMd,
                        }}
                    >
                        <ImgPh
                            src={image.src}
                            label={image.alt}
                            ratio="auto"
                            brightness={0.68}
                            style={{ height: "100%", borderRadius: 0 }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                left: 16,
                                right: 16,
                                bottom: 16,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                padding: "14px 16px",
                                borderRadius: config.theme.radios.intMd,
                                background: "rgba(0,0,0,0.38)",
                                backdropFilter: config.theme.blur.vchNormal,
                            }}
                        >
                            <strong>{image.title}</strong>
                            <Icon.arrowUpRight />
                        </div>
                    </Link>
                ))}
            </div>
        </>
    );

    return (
        <ContenedorBloque bloque={bloque} bitOcultarTitulo={bitBannerCategorias}>
            {contenido}
        </ContenedorBloque>
    );
}

function Productos({
    bloque,
    productos,
    bitNovedades = false,
    bitCarousel = false,
}: {
    bloque: BloqueHomeDTO;
    productos: Product[];
    bitNovedades?: boolean;
    bitCarousel?: boolean;
}) {
    const { config } = useTemplateConfig();
    const intColumnas = useColumnas();
    const lista = completarProductos(productos, bloque.config.intCantidad ?? 4).slice(
        0,
        bloque.config.intCantidad ?? 8,
    );

    if (lista.length === 0) return null;

    const tarjetas = lista.map((producto, index) => (
        <ProductCard
            key={`${producto.id}-${bloque.vchTipo}-${index}`}
            product={producto}
            layout={config.componentes.vchTipoProductCard === "compacta" ? "row" : "card"}
            tipoCard={config.componentes.vchTipoProductCard}
            variant={
                config.componentes.vchTipoProductCard === "editorial" ? "showcase-large" : undefined
            }
            showDiscount={config.componentes.bitMostrarBadges && (bitNovedades || index < 2)}
            discountText={bitNovedades ? "Nuevo" : "Top"}
        />
    ));

    if (bitCarousel) {
        return (
            <ContenedorBloque bloque={bloque}>
                <div style={{ position: "relative" }}>
                    <button
                        type="button"
                        aria-label="Anterior"
                        className="tb-destacados-prev"
                        style={{
                            position: "absolute",
                            left: -18,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 2,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: `1px solid ${config.theme.colores.vchBorde}`,
                            background: config.theme.cards.vchFondo,
                            color: config.theme.colores.vchTextoPrincipal,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Icon.arrowLeft />
                    </button>
                    <button
                        type="button"
                        aria-label="Siguiente"
                        className="tb-destacados-next"
                        style={{
                            position: "absolute",
                            right: -18,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 2,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: `1px solid ${config.theme.colores.vchBorde}`,
                            background: config.theme.cards.vchFondo,
                            color: config.theme.colores.vchTextoPrincipal,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Icon.arrowRight />
                    </button>
                    <Swiper
                        modules={[Navigation]}
                        slidesPerView={
                            config.componentes.vchTipoProductCard === "compacta" ? 1 : intColumnas
                        }
                        spaceBetween={config.theme.espaciado.intGridGap}
                        navigation={{
                            prevEl: ".tb-destacados-prev",
                            nextEl: ".tb-destacados-next",
                        }}
                    >
                        {tarjetas.map((tarjeta) => (
                            <SwiperSlide key={tarjeta.key}>{tarjeta}</SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </ContenedorBloque>
        );
    }

    return (
        <ContenedorBloque bloque={bloque}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        config.componentes.vchTipoProductCard === "compacta"
                            ? "1fr"
                            : `repeat(${intColumnas}, minmax(0, 1fr))`,
                    gap: config.theme.espaciado.intGridGap,
                }}
            >
                {tarjetas}
            </div>
        </ContenedorBloque>
    );
}

function Banner({ bloque }: { bloque: BloqueHomeDTO }) {
    const { config } = useTemplateConfig();
    const bitOscuro = bloque.config.bitOscuro ?? false;

    return (
        <ContenedorBloque bloque={bloque} bitOcultarTitulo>
            <div
                style={{
                    position: "relative",
                    borderRadius: config.theme.radios.intXl,
                    padding: "clamp(32px, 6vw, 64px)",
                    background:
                        bloque.estilo?.vchColorFondo ??
                        (bitOscuro
                            ? config.theme.gradientes.vchDestacado
                            : config.theme.gradientes.vchHero),
                    color:
                        bloque.estilo?.vchColorTexto ??
                        (bitOscuro
                            ? config.theme.botones.vchPrimaryTexto
                            : config.theme.colores.vchTextoPrincipal),
                    boxShadow: config.theme.sombras.vchMd,
                    overflow: "hidden",
                }}
            >
                {bloque.config.vchImagen && (
                    <ImgPh
                        src={bloque.config.vchImagen}
                        ratio="auto"
                        brightness={bitOscuro ? 0.55 : 0.72}
                        style={{ position: "absolute", inset: 0, height: "100%", borderRadius: 0 }}
                    />
                )}
                <div style={{ position: "relative", zIndex: 1 }}>
                    {bloque.config.vchEyebrow && (
                        <div className="eyebrow" style={{ marginBottom: 12 }}>
                            {bloque.config.vchEyebrow}
                        </div>
                    )}
                    <h2 className="h-section" style={{ margin: 0, color: "inherit" }}>
                        {bloque.config.vchTitulo}
                    </h2>
                    {bloque.config.vchSubtitulo && (
                        <p style={{ maxWidth: 660, lineHeight: 1.7, opacity: 0.78 }}>
                            {bloque.config.vchSubtitulo}
                        </p>
                    )}
                    <a className="pill" href={bloque.config.vchLinkBoton ?? "/productos"}>
                        {bloque.config.vchTextoBoton ?? "Ver catálogo"} <Icon.arrowUpRight />
                    </a>
                </div>
            </div>
        </ContenedorBloque>
    );
}

const ICONOS_CONFIANZA: Record<string, React.ReactNode> = {
    truck: <Icon.truck />,
    shield: <Icon.shield />,
    spark: <Icon.spark />,
};

const ITEMS_CONFIANZA_DEFAULT = [
    { vchIcono: "truck", vchTitulo: "Envios", vchDescripcion: "Opciones segun zona" },
    { vchIcono: "shield", vchTitulo: "Compra segura", vchDescripcion: "Checkout protegido" },
    { vchIcono: "spark", vchTitulo: "Atencion", vchDescripcion: "Consulta antes de comprar" },
];

function Confianza({ bloque }: { bloque: BloqueHomeDTO }) {
    const { config } = useTemplateConfig();
    const itemsConfig = bloque.config.vchItems
        ? (JSON.parse(bloque.config.vchItems) as typeof ITEMS_CONFIANZA_DEFAULT)
        : ITEMS_CONFIANZA_DEFAULT;
    const items = itemsConfig.map((it) => ({
        icon: ICONOS_CONFIANZA[it.vchIcono] ?? <Icon.spark />,
        title: it.vchTitulo,
        sub: it.vchDescripcion,
    }));

    return (
        <ContenedorBloque bloque={bloque} bitOcultarTitulo>
            <div
                className="lg-strong"
                style={{
                    borderRadius: config.theme.radios.intLg,
                    padding: 24,
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 16,
                }}
            >
                {items.map((item) => (
                    <div
                        key={item.title}
                        style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                        <span
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: config.theme.radios.intMd,
                                display: "grid",
                                placeItems: "center",
                                background: config.theme.colores.vchSeleccion,
                                color: config.theme.colores.vchMarcaPrincipal,
                            }}
                        >
                            {item.icon}
                        </span>
                        <span>
                            <strong style={{ display: "block" }}>{item.title}</strong>
                            <small style={{ color: "var(--ink-500)" }}>{item.sub}</small>
                        </span>
                    </div>
                ))}
            </div>
        </ContenedorBloque>
    );
}

function Newsletter({ bloque }: { bloque: BloqueHomeDTO }) {
    const { config } = useTemplateConfig();
    if (config.componentes.vchTipoNewsletter === "oculto") return null;

    return (
        <ContenedorBloque bloque={bloque} bitOcultarTitulo>
            <div
                style={{
                    borderRadius: config.theme.radios.intXl,
                    padding: "38px 28px",
                    background: bloque.estilo?.vchColorFondo ?? config.theme.cards.vchFondo,
                    border: `1px solid ${config.theme.cards.vchBorde}`,
                    boxShadow: config.theme.cards.vchSombra,
                    textAlign: "center",
                    ...(bloque.estilo?.vchColorTexto && { color: bloque.estilo.vchColorTexto }),
                }}
            >
                <h2 className="h-section" style={{ margin: 0 }}>
                    {bloque.config.vchTitulo}
                </h2>
                {bloque.config.vchSubtitulo && (
                    <p style={{ color: "var(--ink-500)", margin: "12px auto 0", maxWidth: 520 }}>
                        {bloque.config.vchSubtitulo}
                    </p>
                )}
            </div>
        </ContenedorBloque>
    );
}

function EditableOverlay({
    bloque,
    children,
}: {
    bloque: BloqueHomeDTO;
    children: React.ReactNode;
}) {
    const { bitContentEditMode, emitirSeleccion } = useEditableBlock(bloque);
    if (!bitContentEditMode) return <>{children}</>;

    return (
        <div
            onClickCapture={(event) => {
                event.preventDefault();
                event.stopPropagation();
                emitirSeleccion();
            }}
            style={{
                position: "relative",
                cursor: "pointer",
                opacity: bloque.bitActivo ? 1 : 0.45,
                outline: "2px dashed transparent",
                outlineOffset: 2,
                transition: "outline-color 0.15s",
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.outlineColor = "#6366f1";
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.outlineColor = "transparent";
            }}
        >
            {children}
            <span
                style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 100,
                    background: "#6366f1",
                    color: "#fff",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    pointerEvents: "none",
                    opacity: 0.92,
                }}
            >
                ✏ {bloque.vchTipo}
                {!bloque.bitActivo && " · Oculta"}
            </span>
        </div>
    );
}

export default function TemplateRenderer() {
    const { config, bitContentEditMode } = useTemplateConfig();
    const { showcaseProducts, novedadesProducts, loading, error } = useHomeProducts();
    const bloques = useMemo(
        () =>
            [...config.bloquesHome]
                .filter((bloque) => bloque.bitActivo || bitContentEditMode)
                .sort((a, b) => a.intOrden - b.intOrden),
        [config.bloquesHome, bitContentEditMode],
    );

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress size={54} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Cargando productos...
                </Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <Typography variant="h4" color="error" gutterBottom>
                    {error}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    No pudimos cargar los productos en este momento.
                </Typography>
            </Container>
        );
    }

    return (
        <>
            {bloques.map((bloque) => {
                const key = bloque.uniIdBloque;

                let contenido: React.ReactNode = null;
                if (bloque.vchTipo === "hero")
                    contenido = <Hero bloque={bloque} productos={showcaseProducts} />;
                else if (bloque.vchTipo === "categorias")
                    contenido = <Categorias bloque={bloque} />;
                else if (bloque.vchTipo === "productosDestacados")
                    contenido = (
                        <Productos bloque={bloque} productos={showcaseProducts} bitCarousel />
                    );
                else if (bloque.vchTipo === "novedades")
                    contenido = (
                        <Productos bloque={bloque} productos={novedadesProducts} bitNovedades />
                    );
                else if (bloque.vchTipo === "bannerEditorial" || bloque.vchTipo === "about")
                    contenido = <Banner bloque={bloque} />;
                else if (bloque.vchTipo === "confianza") contenido = <Confianza bloque={bloque} />;
                else if (bloque.vchTipo === "newsletter")
                    contenido = <Newsletter bloque={bloque} />;

                if (!contenido) return null;

                return (
                    <EditableOverlay key={key} bloque={bloque}>
                        {contenido}
                    </EditableOverlay>
                );
            })}
        </>
    );
}
